// PATH: src/pages/TilmidPlusConfigPage.tsx
import React from "react";
import { Alert, Box, Button, Card, Skeleton, Stack, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/SaveRounded";
import RestartAltIcon from "@mui/icons-material/RestartAltRounded";
import ForumIcon from "@mui/icons-material/ForumRounded";
import MenuBookIcon from "@mui/icons-material/MenuBookOutlined";
import FactCheckIcon from "@mui/icons-material/FactCheckOutlined";
import EditNoteIcon from "@mui/icons-material/EditNoteRounded";
import RateReviewIcon from "@mui/icons-material/RateReviewOutlined";
import FlagIcon from "@mui/icons-material/FlagOutlined";
import DocumentScannerIcon from "@mui/icons-material/DocumentScannerOutlined";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import MicIcon from "@mui/icons-material/MicNoneOutlined";
import HistoryEduIcon from "@mui/icons-material/HistoryEduOutlined";
import { useAppConfigsApi, useCurriculumApi } from "../api";
import type {
  AppConfig,
  CatalogModel,
  LlmCatalog,
  LlmCostEstimates,
  LlmRole,
  LlmSettings,
  QuestPromptsResponse,
  TilmidPlusConfig,
} from "../models/domain";
import { PageHeader, StatusPill, errorMessage, useConfirm, useToast } from "../components/ui";
import RoleAgentCard from "../components/settings/RoleAgentCard";
import DeclareModelDialog, { type DeclareTarget } from "../components/settings/DeclareModelDialog";
import { rolePrompts } from "../components/settings/rolePrompts";
import { useEmbedPrompts } from "../components/settings/useEmbedPrompts";
import SubjectSettingsTable, { type SubjectRow } from "../components/settings/SubjectSettingsTable";
import { capsOnly } from "../components/settings/llmCatalog";
import {
  ROLES,
  globalRoleChanged,
  impact,
  reviewEnabled,
  sameSettings,
  setReviewEnabled,
  type RoleMeta,
} from "../components/settings/llmSettings";

const EMPTY_LLM: LlmSettings = { global: {} };

/** Le catalogue rendu par le serveur, posé dans un bloc `llm` (forme de l'app-config). */
const withCatalog = (llm: LlmSettings, catalog: Pick<LlmCatalog, "providers" | "models">): LlmSettings => ({
  ...llm,
  providers: catalog.providers,
  models: capsOnly(catalog.models),
});

/** Les deux relectures qui se coupent : celles dont le rôle dit ce que la coupure fait aux élèves. */
const SWITCHES = ROLES.filter((r): r is RoleMeta & { switchOff: string } => Boolean(r.switchOff));

const ICONS: Record<LlmRole, React.ReactNode> = {
  objective_extraction: <FlagIcon />,
  fact_extraction: <MenuBookIcon />,
  fact_review: <FactCheckIcon />,
  quest_generation: <EditNoteIcon />,
  quest_review: <RateReviewIcon />,
  course_writing: <HistoryEduIcon />,
  companion: <ForumIcon />,
  ocr: <DocumentScannerIcon />,
  tts: <RecordVoiceOverIcon />,
  stt: <MicIcon />,
};

/** Une liste lisible de noms : les trois premiers, puis « et N autres ». */
const names = (ids: string[], byId: Map<string, string>) => {
  const n = ids.map((id) => byId.get(id) ?? id);
  return n.length <= 3 ? n.join(", ") : `${n.slice(0, 3).join(", ")} et ${n.length - 3} autre(s)`;
};

/**
 * Réglages des agents IA de Tilmid+ : les rôles des objectifs, des faits et
 * des quêtes — au global et par matière —, le compagnon, l'OCR et la voix. Une seule
 * config, `tilmid-plus` ; le serveur refuse un couple fournisseur/modèle non
 * déclaré, non tarifé ou qui ne fait pas ce que le rôle demande.
 */
const TilmidPlusConfigPage: React.FC = () => {
  const { getTilmidPlusConfig, updateTilmidPlusConfig, getLlmCatalog, getQuestPrompts, getCostEstimates } =
    useAppConfigsApi();
  const { getCurriculumOverview } = useCurriculumApi();
  const toast = useToast();
  const confirm = useConfirm();

  const [entry, setEntry] = React.useState<AppConfig<TilmidPlusConfig> | null>(null);
  const [llm, setLlm] = React.useState<LlmSettings>(EMPTY_LLM);
  const [subjects, setSubjects] = React.useState<SubjectRow[]>([]);
  // Tarifs des modèles déclarés : « Déclarer » repart de ceux d'un modèle connu.
  const [catalogModels, setCatalogModels] = React.useState<Record<string, CatalogModel>>({});
  const [declaring, setDeclaring] = React.useState<DeclareTarget | null>(null);
  // Ce qui éclaire les cartes sans être nécessaire au réglage : chargé à côté,
  // une panne s'affiche dans la carte et n'empêche pas d'enregistrer.
  const { prompts: factPrompts, error: factPromptsError } = useEmbedPrompts();
  const [questPrompts, setQuestPrompts] = React.useState<QuestPromptsResponse | null>(null);
  const [estimates, setEstimates] = React.useState<LlmCostEstimates | null>(null);
  const [sideErrors, setSideErrors] = React.useState<{ quests: string | null; costs: string | null }>({
    quests: null,
    costs: null,
  });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ data }, curriculum, catalog] = await Promise.all([
        getTilmidPlusConfig(),
        getCurriculumOverview(),
        // Sans catalogue (tarifs), « Déclarer » part de valeurs vides : le
        // réglage, lui, reste possible.
        getLlmCatalog().catch(() => null),
      ]);
      setEntry(data);
      setLlm(data?.value?.llm ?? EMPTY_LLM);
      setCatalogModels(catalog?.data.models ?? {});
      setSubjects(
        curriculum.data.programs.flatMap((p) =>
          p.subjects.map((s) => ({ id: s.id, name: s.name, program: p.program.name || p.program.code })),
        ),
      );
    } catch (e) {
      setError(errorMessage(e, "Impossible de charger la configuration."));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    getQuestPrompts()
      .then(({ data }) => setQuestPrompts(data))
      .catch((e) => setSideErrors((x) => ({ ...x, quests: errorMessage(e, "Prompts des quêtes indisponibles (tc_learn).") })));
    getCostEstimates()
      .then(({ data }) => setEstimates(data))
      .catch((e) => setSideErrors((x) => ({ ...x, costs: errorMessage(e, "Estimation des coûts indisponible.") })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savedLlm = entry?.value?.llm ?? EMPTY_LLM;
  const companionChanged = globalRoleChanged(llm, savedLlm, "companion");
  const llmChanged = !sameSettings(llm, savedLlm);
  const anyDirty = llmChanged;

  // Un modèle déclaré depuis cette page entre dans le catalogue ENREGISTRÉ
  // (le serveur l'a écrit) : il est posé des deux côtés, pour ne pas compter
  // comme une modification à appliquer.
  const declared = (catalog: LlmCatalog) => {
    setCatalogModels(catalog.models);
    setLlm((l) => withCatalog(l, catalog));
    setEntry((e) => (e ? { ...e, value: { ...e.value, llm: withCatalog(e.value.llm ?? EMPTY_LLM, catalog) } } : e));
  };
  const subjectNames = React.useMemo(() => new Map(subjects.map((s) => [s.id, s.name])), [subjects]);

  // Coupure : confirmée tout de suite (et redemandée à l'enregistrement, avec
  // le reste). Rétablir ne demande rien.
  const toggle = async ({ role, label, switchOff: off }: RoleMeta, enabled: boolean) => {
    if (!enabled) {
      const ok = await confirm({ title: `Couper la ${label.toLowerCase()} ?`, message: off, confirmLabel: "Couper", danger: true });
      if (!ok) return;
    }
    setLlm((l) => setReviewEnabled(l, role, enabled));
  };

  const save = async () => {
    if (!entry?.value) return;
    const effet = impact(savedLlm, llm, subjects.map((s) => s.id));
    const coupees = SWITCHES.filter((s) => reviewEnabled(savedLlm, s.role) && !reviewEnabled(llm, s.role));
    const message = (
      <Stack spacing={1.25}>
        {companionChanged && <Typography variant="body2">Le compagnon change au message suivant, pour tous les élèves.</Typography>}
        {effet.quests.length > 0 && (
          <Alert severity="warning">
            Les quêtes partagées {effet.questsGlobal ? "de toutes les matières qui suivent le global" : `de ${names(effet.quests, subjectNames)}`} seront
            régénérées : les premières quêtes de ces matières seront plus lentes à préparer, et payées à nouveau.
          </Alert>
        )}
        {effet.objectives.length > 0 && (
          <Typography variant="body2">
            Les objectifs{" "}
            {effet.objectivesGlobal ? "de toutes les matières qui suivent le global" : `de ${names(effet.objectives, subjectNames)}`} seront
            extraits avec ce réglage au prochain import ou à la prochaine extraction demandée. Rien n'est relancé, aucun objectif servi
            ni aucune progression d'élève ne change.
          </Typography>
        )}
        {effet.facts.length > 0 && (
          <Typography variant="body2">
            Les faits {effet.factsGlobal ? "de toutes les matières qui suivent le global" : `de ${names(effet.facts, subjectNames)}`} seront
            ré-extraits au prochain run d'extraction (rien n'est relancé automatiquement).
          </Typography>
        )}
        {coupees.map((s) => (
          <Alert key={s.role} severity="error">
            {s.label} coupée : {s.switchOff}
          </Alert>
        ))}
        {llmChanged && effet.quests.length === 0 && effet.facts.length === 0 && effet.objectives.length === 0 && coupees.length === 0 && (
          <Typography variant="body2">Les nouveaux réglages valent pour les prochains appels.</Typography>
        )}
      </Stack>
    );
    const ok = await confirm({
      title: "Appliquer les réglages ?",
      message,
      confirmLabel: "Appliquer",
      danger: coupees.length > 0,
    });
    if (!ok) return;
    setSaving(true);
    try {
      // Le catalogue (fournisseurs, modèles) se règle sur l'écran
      // « Fournisseurs » : on enregistre celui du serveur À CET INSTANT, pas
      // la copie chargée avec la page, pour ne pas défaire une déclaration
      // faite entre-temps dans un autre onglet.
      const { data: fresh } = await getTilmidPlusConfig();
      const freshLlm = fresh.value.llm ?? EMPTY_LLM;
      const companion = llm.global.companion;
      const { data } = await updateTilmidPlusConfig({
        ...entry.value,
        // Copie du rôle `companion` : le serveur la recalcule de toute façon.
        model_name: companion?.model ?? "",
        temperature: companion?.temperature ?? 0,
        llm: { ...llm, providers: freshLlm.providers, models: freshLlm.models },
      });
      setEntry(data);
      setLlm(data.value.llm ?? EMPTY_LLM);
      toast.success("Réglages appliqués.");
    } catch (e) {
      toast.error(errorMessage(e, "Échec de l'enregistrement."));
    } finally {
      setSaving(false);
    }
  };

  // Bandeau rouge sur l'état ENREGISTRÉ : c'est ce que les élèves vivent.
  const savedOff = SWITCHES.filter((s) => entry && !reviewEnabled(savedLlm, s.role));

  return (
    <Box sx={{ maxWidth: 1280 }}>
      <PageHeader
        title="Réglages Tilmid+"
        subtitle="Les agents IA de l'application élève, un par carte : ce qu'il fait, son fournisseur et son modèle, ce qu'il coûte, sa consigne et le prompt de base qu'elle complète. Sous les cartes, les seules matières réglées autrement. Fournisseurs, clés et modèles déclarés : écran « Fournisseurs »."
      />

      {savedOff.map((s) => (
        <Alert key={s.role} severity="error" variant="filled" sx={{ mb: 2 }}>
          {s.label} coupée. {s.switchOff}
        </Alert>
      ))}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void load()}>
              Réessayer
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack spacing={2}>
          {[0, 1].map((i) => (
            <Card key={i} sx={{ p: 2.5 }}>
              <Skeleton width="40%" height={32} />
              <Skeleton height={56} />
              <Skeleton height={56} />
            </Card>
          ))}
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, alignItems: "start" }}>
            {/* Les objectifs d'abord, sur toute la largeur (première étape, sans
                relecteur à part) ; puis faits et quêtes, l'agent et son
                relecteur côte à côte ; le compagnon, l'OCR et la voix ensuite. */}
            {ROLES.map((meta) => (
              <Box key={meta.role} sx={{ gridColumn: meta.role === "objective_extraction" ? "1 / -1" : undefined }}>
                <RoleAgentCard
                  meta={meta}
                  icon={ICONS[meta.role]}
                  llm={llm}
                  saved={savedLlm}
                  onChange={setLlm}
                  onToggle={(enabled) => void toggle(meta, enabled)}
                  onDeclare={(provider, model) => setDeclaring({ provider, model })}
                  estimates={estimates}
                  estimatesError={sideErrors.costs}
                  prompts={
                    meta.basePrompt
                      ? rolePrompts(meta.role, factPrompts, questPrompts, { facts: factPromptsError, quests: sideErrors.quests })
                      : null
                  }
                  disabled={!entry}
                />
              </Box>
            ))}
          </Box>

          <SubjectSettingsTable
            llm={llm}
            onChange={setLlm}
            subjects={subjects}
            onDeclare={(provider, model) => setDeclaring({ provider, model })}
            estimates={estimates}
            disabled={!entry}
          />
        </Stack>
      )}

      <Card sx={{ mt: 2, position: "sticky", bottom: 0, zIndex: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1.5}
          sx={{ px: 2.5, py: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {entry && (
              <>
                <StatusPill tone={anyDirty ? "warning" : "success"}>
                  {anyDirty ? "Modifications non appliquées" : "À jour"}
                </StatusPill>
                <Typography variant="caption" color="text.secondary">
                  version {entry.version} ·{" "}
                  {new Date(entry.updated_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}
                </Typography>
              </>
            )}
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              disabled={!anyDirty || saving}
              onClick={() => setLlm(savedLlm)}
            >
              Annuler
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} disabled={!anyDirty || saving} onClick={() => void save()}>
              {saving ? "Application…" : "Appliquer"}
            </Button>
          </Stack>
        </Stack>
      </Card>
      <DeclareModelDialog
        target={declaring}
        providers={llm.providers ?? {}}
        models={catalogModels}
        onClose={() => setDeclaring(null)}
        onDeclared={(catalog) => {
          declared(catalog);
          toast.success("Modèle déclaré.");
        }}
      />
    </Box>
  );
};

export default TilmidPlusConfigPage;
